import type { DocSyncRequestSource } from '@affine/nbstore';
import type { Workspace } from '@blocksuite/affine/store';
import { DATABASE_ROW_SHARD_ROWS_MAP_KEY } from '@streamlain/database-identity';

import { workspaceApiFetch } from '../../cloud/services/workspace-api-client';
import {
  collectDatabaseSystemDocAccessBindingsFromPage,
  type DatabaseSystemDocAccessBinding,
} from './database-subdoc-preload';

export type { DatabaseSystemDocAccessBinding };

type DatabaseSystemDocSyncEngine = {
  addDoc?: (docId: string, source: DocSyncRequestSource) => void;
  addPriority?: (docId: string, priority: number) => () => void;
  flushAndWaitForSynced?: (docId: string, abort?: AbortSignal) => Promise<void>;
};

const DURABLE_WRITE_SYNC_PRIORITY = 1_000;
// A foreground write can overlap the initial root/shard preload after
// navigation. That preload deliberately has a finite 30-second window, so the
// stricter durable-write boundary must allow the same window while the shared
// sync queue is still making progress.
const DURABLE_WRITE_SYNC_TIMEOUT_MS = 30_000;

async function readError(response: Response, fallback: string) {
  const message = await response.text();
  return message || fallback;
}

export async function postDatabaseSystemDocAccessBindings(input: {
  workspaceId: string;
  bindings: readonly DatabaseSystemDocAccessBinding[];
  awaitPersistence?: boolean;
  signal?: AbortSignal;
}): Promise<void> {
  if (input.bindings.length === 0) return;

  const response = await workspaceApiFetch(
    `/api/workspace/${encodeURIComponent(input.workspaceId)}/database-imports/system-doc-access`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bindings: input.bindings,
        ...(input.awaitPersistence ? { awaitPersistence: true } : {}),
      }),
      signal: input.signal,
    },
    {
      workspaceId: input.workspaceId,
      category: 'workspace-api',
      key: 'database-system-doc-access',
      mode: 'foreground',
      priority: 'foreground',
    }
  );

  if (!response.ok) {
    throw new Error(
      await readError(
        response,
        `Database system doc access registration failed (${response.status})`
      )
    );
  }
}

export async function flushDatabaseSystemDocsForDurableWrite(input: {
  collection: Workspace;
  docEngine: DatabaseSystemDocSyncEngine;
  docId: string;
  workspaceId: string;
  workspaceFirst?: boolean;
  databaseIds?: readonly string[];
  includeParentDocs?: boolean;
  awaitServerPersistence?: boolean;
}): Promise<readonly DatabaseSystemDocAccessBinding[]> {
  const pageDoc = input.collection.getDoc(input.docId);
  if (!pageDoc) {
    throw new Error(`Document "${input.docId}" is not available for sync.`);
  }
  if (!pageDoc.ready) {
    pageDoc.load();
  }
  const page = pageDoc.getStore({ id: input.docId });
  const requestedDatabaseIds = input.databaseIds
    ? new Set(input.databaseIds)
    : undefined;
  const discoveredBindings = collectDatabaseSystemDocAccessBindingsFromPage(
    page,
    docId => {
      const doc = input.collection.getDoc(docId);
      if (!doc) return null;
      if (!doc.ready) {
        doc.load();
      }
      return doc.getStore({ id: docId });
    },
    requestedDatabaseIds
  );
  const bindings = discoveredBindings;
  if (
    requestedDatabaseIds &&
    new Set(bindings.map(binding => binding.databaseId)).size !==
      requestedDatabaseIds.size
  ) {
    const missing = [...requestedDatabaseIds].filter(
      databaseId => !bindings.some(binding => binding.databaseId === databaseId)
    );
    throw new Error(
      `Database system docs are not available for: ${missing.join(', ')}`
    );
  }
  const hasMaterializedLocalDoc = (docId: string): boolean => {
    const doc = input.collection.getDoc(docId);
    if (!doc) return false;
    if (!doc.ready) {
      doc.load();
    }
    const store = doc.getStore({ id: docId });
    return (
      store.doc.yBlocks.size > 0 ||
      store.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size > 0
    );
  };
  if (!input.docEngine.flushAndWaitForSynced) {
    throw new Error('Document sync engine is not available.');
  }
  const flushAndWaitForSynced = input.docEngine.flushAndWaitForSynced;

  const localSystemDocIds = bindings.flatMap(binding => [
    binding.rootDocId,
    ...binding.manifest.shards
      .map(shard => shard.serverDocId)
      // A new empty sharded database has a deterministic default shard in its
      // access manifest before that row-shard document exists. Never wait for
      // sync on a phantom doc; the binding still carries the complete
      // deterministic manifest for later rows.
      .filter(hasMaterializedLocalDoc),
  ]);
  const includeParentDocs = input.includeParentDocs !== false;
  const docIds = new Set([
    ...(includeParentDocs ? [input.workspaceId, input.docId] : []),
    ...localSystemDocIds,
  ]);
  const releasePriorities: Array<() => void> = [];
  for (const docId of docIds) {
    input.docEngine.addDoc?.(docId, 'permission-mutation-preflight');
    const releasePriority = input.docEngine.addPriority?.(
      docId,
      DURABLE_WRITE_SYNC_PRIORITY
    );
    if (releasePriority) {
      releasePriorities.push(releasePriority);
    }
  }
  const flushDoc = async (docId: string) => {
    const startedAt =
      typeof performance !== 'undefined' ? performance.now() : null;
    try {
      await flushAndWaitForSynced(
        docId,
        AbortSignal.timeout(DURABLE_WRITE_SYNC_TIMEOUT_MS)
      );
    } catch (error) {
      const detail =
        error instanceof Error ? error.message : 'unknown sync failure';
      throw new Error(`Document "${docId}" sync failed: ${detail}`, {
        cause: error,
      });
    } finally {
      if (startedAt !== null && typeof performance.measure === 'function') {
        performance.measure(`custom-app-durable-sync:${docId}`, {
          start: startedAt,
          end: performance.now(),
        });
      }
    }
  };
  // Server mutations involving newly created docs require the parent-first
  // boundary. Existing database content writes can skip that unchanged parent
  // roundtrip and only need the final parent-after-children acknowledgement.
  try {
    if (includeParentDocs && input.workspaceFirst !== false) {
      await flushDoc(input.workspaceId);
    }
    await Promise.all(
      [...docIds]
        .filter(docId => docId !== input.workspaceId)
        .map(docId => flushDoc(docId))
    );
    if (includeParentDocs) {
      await flushDoc(input.workspaceId);
    }
    // A sync ACK proves that the backend accepted and enqueued the update, not
    // that DocPersist already materialized it. Server-side consumers such as
    // Custom App publish must not read until that captured outbox tail is
    // applied to docs/doc_snapshots.
    if (input.awaitServerPersistence && bindings.length > 0) {
      await postDatabaseSystemDocAccessBindings({
        workspaceId: input.workspaceId,
        bindings,
        awaitPersistence: true,
      });
    }
  } finally {
    releasePriorities.forEach(release => release());
  }
  return bindings;
}

export async function prepareDatabaseSystemDocsForServerMutation(input: {
  collection: Workspace;
  docEngine: DatabaseSystemDocSyncEngine;
  docId: string;
  workspaceId: string;
}): Promise<void> {
  const bindings = await flushDatabaseSystemDocsForDurableWrite(input);
  if (bindings.length === 0) return;
  await postDatabaseSystemDocAccessBindings({
    workspaceId: input.workspaceId,
    bindings,
  });
}
